import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../../layout/default";
import axios from "axios";
import { Button, Spinner, Container, Row, Col, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

// const baseURL = "http://127.0.0.1:8000/storage/";
const Cart = () => {
  const [cartData, setCartData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subtotal, setSubtotal] = useState(0);
  const [savings, setSavings] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const user_id = localStorage.getItem("user_id");
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      navigate('/shop/auths/login', { replace: true });
    }

    if (!user_id || !authToken) {
      Swal.fire({
        icon: "warning",
        title: "Not Logged In",
        text: "Please login first.",
      }).then(() => navigate("/shop/auths/login"));
      return;
    }

    fetchCartData(user_id);
  }, []);

  const fetchCartData = async (user_id) => {
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(`${process.env.REACT_APP_HOST}api/cart/user/${user_id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.data.success) {
        setCartData(response.data.data);
        calculateTotals(response.data.data);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch cart data.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "info",
        title: "Empty",
        text: "There is no product in the cart.",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (data) => {
    let total = 0; // Subtotal
    let discountTotal = 0; // Total Savings

    data.forEach((item) => {
      const itemPrice = parseFloat(item.price.toFixed(2));
      const itemQuantity = item.quantity;
      const itemDiscount = item.discount;

      total += itemPrice * itemQuantity;
      discountTotal += (itemPrice * itemQuantity * itemDiscount) / 100;
    });

    total = parseFloat(total.toFixed(2));
    discountTotal = parseFloat(discountTotal.toFixed(2));

    setSubtotal(total);
    setSavings(discountTotal);
    setGrandTotal(parseFloat((total - discountTotal).toFixed(2))); // Subtotal minus Savings
  };

  const truncateText = (text, maxLength = 40) =>
    text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

  const updateQuantity = (id, quantity) => {
    const updatedCart = cartData.map((item) => {
      if (item.id === id) {
        const newQuantity = Math.max(1, quantity); // Ensure quantity is at least 1
        return {
          ...item,
          quantity: newQuantity,
        };
      }
      return item;
    });

    setCartData(updatedCart);
    calculateTotals(updatedCart); // Recalculate overall totals
  };

  const deleteItem = async (id) => {
    const authToken = localStorage.getItem("authToken");
    try {
      await axios.delete(`${process.env.REACT_APP_HOST}api/cart/${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const updatedCart = cartData.filter((item) => item.id !== id);
      setCartData(updatedCart);
      calculateTotals(updatedCart); // Recalculate totals after deletion
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete the item.",
      });
    }
  };

  const handleSelect = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const buySelected = () => {
    const itemsToBuy = cartData.filter((item) => selectedItems.includes(item.id));
    if (itemsToBuy.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Items Selected",
        text: "Please select items to buy.",
      });
      return;
    }
    const user_id = localStorage.getItem("user_id");
    const checkoutData = {
      user_id,
      total_amount: subtotal,
      discount: savings,
      grandTotal,
      cartData: itemsToBuy,
    };
    navigate("/buy", { state: checkoutData });
  };

  const buyAll = () => {
    if (cartData.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Empty Cart",
        text: "Your cart is empty.",
      });
      return;
    }

    const user_id = localStorage.getItem("user_id");
    const checkoutData = {
      user_id,
      total_amount: subtotal,
      discount: savings,
      grandTotal,
      cartData,
    };
    navigate("/shop/auths/CheckOut", { state: checkoutData });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Layout title="Cart">
      <Container className="mt-4">
        <h1 className="mb-4">Your Basket</h1>

        <div className="bg-dark text-white p-3 mb-4 rounded">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="h5 text-white">Subtotal ({cartData.length} items):</span>
            <span className="h5 text-white">₹{subtotal.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span className="h5 text-white">Savings:</span>
            <span className="h5 text-success">₹{savings.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span className="h5 text-white">Grand Total:</span>
            <span className="h5 text-warning">₹{grandTotal.toFixed(2)}</span>
          </div>
          <div className="text-end mt-3">
            <Button variant="danger" onClick={buyAll} className="w-100">
              Checkout
            </Button>
          </div>
        </div>

        {cartData.length === 0 ? (
          <div className="text-center">
            <h5>Your cart is empty.</h5>
          </div>
        ) : (
          <Row>
            {cartData.map((item) => {
              const totalPrice = parseFloat((item.price * item.quantity).toFixed(2));
              const discountedPrice = parseFloat((item.price * (1 - item.discount / 100) * item.quantity).toFixed(2));
              return (
                <Col xs={12} sm={6} md={4} className="mb-4" key={item.id}>
                  <div className="d-flex flex-column flex-sm-row bg-light p-3 rounded shadow-sm">
                    <img
                      src={`${process.env.REACT_APP_HOST}storage/${item.image_url}`}
                      alt={item.product_title}
                      className="rounded mb-3 mb-sm-0 me-sm-3"
                      style={{ width: "100%", height: "auto", maxWidth: "180px", objectFit: "cover" }}
                    />
                    <div className="flex-grow-1">
                      <h5 className="text-truncate">{truncateText(item.product_title)}</h5>
                      <p className="mb-2 text-muted">{truncateText(item.description)}</p>
                      <p>
                        <span>{item.unit_quantity}{item.unit_title}</span><br />
                        <span className="fw-bold">Original Price:</span> ₹{item.price.toFixed(2)} <br />
                        <span className="fw-bold">Discount:</span> {item.discount}% <br />
                        <span className="fw-bold text-success">Price After Discount:</span> ₹{discountedPrice.toFixed(2)} <br />
                        <span className="fw-bold">Total Price:</span> ₹{totalPrice.toFixed(2)}
                      </p>
                      <div className="d-flex align-items-center mb-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          -
                        </Button>
                        <span className="mx-2">{item.quantity}</span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </Button>
                      </div>
                      <div className="d-flex justify-content-between">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => deleteItem(item.id)}
                        >
                          Remove
                        </Button>
                        <Form.Check
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelect(item.id)}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* <div className="d-flex flex-column flex-md-row justify-content-between mt-4">
          <Button variant="success" onClick={buySelected} className="mb-2 mb-md-0">
            Buy Selected
          </Button>
          <Button variant="primary" onClick={buyAll}>
            Buy All
          </Button>
        </div> */}
      </Container>
    </Layout>
  );
};

export default Cart;